import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Tooltip } from '@fly/core';
import { copyStringToClipboard } from '@fly/shared';
import { DeployType } from '@fly/store';
import cn from 'classnames';

import {
  DeploymentReleaseDelta,
  type DeploymentReleaseDeltaIntermediate,
} from '../DeploymentReleaseDelta/DeploymentReleaseDelta';

export type DeploymentDeltaProps = {
  deployNumber: number;
  repositoryOwner?: string | null;
  repositoryName?: string | null;
  environmentName?: string;
  /** Opaque wave id; forwarded to `DeploymentReleaseDelta` for deep links. */
  deploymentId?: string;
  /**
   * Current deployment's workflow name + run number. Forwarded to
   * `DeploymentReleaseDelta` so the release-delta deep link scopes the
   * Releases page to the full wave (PR fly-service#1164).
   */
  deploymentWorkflowName?: string;
  deploymentWorkflowNumber?: string;
  /**
   * AI-generated cumulative deployment headline (`DeploymentDelta.cumulativeTitle`).
   * Forwarded to `DeploymentReleaseDelta` for the Releases filter chip label.
   */
  deploymentTitle?: string | null;
  /**
   * Release that landed this deployment wave. Required for the release-delta
   * link to be clickable — when absent the row collapses to a read-only badge.
   */
  deployedRelease?: DeploymentReleaseDeltaIntermediate | null;
  runningArtifactName?: string | null;
  /**
   * Wave classification from the backend. Forwarded to `DeploymentReleaseDelta`
   * to switch between forward and rollback framing for the button label and
   * popover header.
   */
  deployType?: DeployType;
  className?: string;
};

export function DeploymentDelta({
  deployNumber,
  repositoryOwner,
  repositoryName,
  environmentName,
  deploymentId,
  deploymentWorkflowName,
  deploymentWorkflowNumber,
  deploymentTitle,
  deployedRelease,
  runningArtifactName,
  deployType,
  className,
}: DeploymentDeltaProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex flex-row items-center gap-fly-2', className)}>
      <DeploymentReleaseDelta
        deployNumber={deployNumber}
        repositoryOwner={repositoryOwner}
        repositoryName={repositoryName}
        environmentName={environmentName}
        deploymentId={deploymentId}
        deploymentWorkflowName={deploymentWorkflowName}
        deploymentWorkflowNumber={deploymentWorkflowNumber}
        deploymentTitle={deploymentTitle}
        deployedRelease={deployedRelease}
        deployType={deployType}
      />
      {runningArtifactName ? (
        <Tooltip
          content={t('app.common.actions.click_to_copy', {
            defaultValue: 'Click to copy',
          })}
          delayDuration={0}
        >
          <Badge
            color='neutral'
            size='1'
            variant='soft'
            iconStart='docker'
            iconStartSize={12}
            data-testid='runtime-environments-card-artifact-running'
            className='fly-gap-2 max-w-42 min-w-0 flex flex-1 cursor-pointer'
            onClick={(e: MouseEvent<HTMLDivElement>) => {
              e.preventDefault();
              e.stopPropagation();
              void copyStringToClipboard(runningArtifactName);
            }}
          >
            <span className='w-full truncate'>{runningArtifactName}</span>
          </Badge>
        </Tooltip>
      ) : null}
    </div>
  );
}
